import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import api from "../utils/api";
import {
  Plus,
  Clock,
  ArrowRight,
  X,
  Sparkles,
  FileText,
  Plug,
  UserPlus,
  Wrench,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight
} from "lucide-react";
import { useToast } from "../context/ToastContext";
import CustomDropdown from "../components/CustomDropdown";

export default function AdminDashboard({
  activeAiAgentId,
  aiAgents,
  setActiveTab,
  fetchAiAgents,
  setActiveAiAgentId,
  activeChatsCount
}) {
  const { showToast } = useToast();

  // Dashboard states
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("today"); // "today", "7d", "30d"
  const dateRangeLabels = {
    today: "Today",
    "7d": "Last 7 days",
    "30d": "Last 30 days"
  };

  const dateRangeOptions = [
    { value: "today", label: "Today" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" }
  ];

  const [dashboardData, setDashboardData] = useState(null);
  const [, setTokenMetrics] = useState(null);
  const [, setPrevDashboardData] = useState(null);

  // "Needs Attention" live handoff states
  const [handoffSessions, setHandoffSessions] = useState([]);
  const [allHandoffsData, setAllHandoffsData] = useState([]);
  const [claimingId, setClaimingId] = useState(null);
  const [hoveredSessionId, setHoveredSessionId] = useState(null);

  // Modal state for "+ New Agent"
  const [modalOpen, setModalOpen] = useState(false);
  const [newAiAgentName, setNewAiAgentName] = useState("");
  const [newAiAgentDesc, setNewAiAgentDesc] = useState("");
  const [creatingaiAgent, setcreatingaiAgent] = useState(false);

  // Load all dashboard metrics and handoffs
  const loadDashboardData = useCallback(async (showSpinner = true) => {
    if (!activeAiAgentId) {
      setActiveAiAgentId("b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03");
      return;
    }
    if (showSpinner) setLoading(true);
    try {
      const now = new Date();
      let startDateStr = "";
      const endDateStr = now.toISOString();

      if (dateRange === "today") {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startDateStr = today.toISOString();
      } else if (dateRange === "7d") {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        startDateStr = sevenDaysAgo.toISOString();
      } else if (dateRange === "30d") {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        startDateStr = thirtyDaysAgo.toISOString();
      }

      const currentPeriodMs = now.getTime() - new Date(startDateStr).getTime();
      const prevStartDateStr = new Date(new Date(startDateStr).getTime() - currentPeriodMs).toISOString();
      const prevEndDateStr = startDateStr;

      const [overviewData, tokenData, prevOverviewData] = await Promise.all([
        api.request(
          `/analytics/dashboard/overview?start_date=${startDateStr}&end_date=${endDateStr}`,
          { aiAgentId: activeAiAgentId }
        ),
        api.request(
          `/analytics/dashboard/token-analytics?start_date=${startDateStr}&end_date=${endDateStr}`,
          { aiAgentId: activeAiAgentId }
        ),
        api.request(
          `/analytics/dashboard/overview?start_date=${prevStartDateStr}&end_date=${prevEndDateStr}`,
          { aiAgentId: activeAiAgentId }
        ).catch((err) => {
          console.warn("[AdminDashboard] Failed to fetch previous period overview:", err);
          return null;
        })
      ]);

      setDashboardData(overviewData);
      setTokenMetrics(tokenData);
      setPrevDashboardData(prevOverviewData);

      const widgetSessionsRes = await api.request(
        "/widget/sessions?limit=40",
        { aiAgentId: activeAiAgentId }
      );

      if (widgetSessionsRes && widgetSessionsRes.sessions) {
        const attentionRequired = widgetSessionsRes.sessions.filter(
          s => s.handoff_status === "requested"
        );
        setHandoffSessions(attentionRequired);
        
        const allHandoffs = widgetSessionsRes.sessions.filter(
          s => s.handoff_status === "requested" || s.handoff_status === "active" || s.handoff_status === "claimed"
        );
        setAllHandoffsData(allHandoffs);
      }
    } catch (error) {
      console.error("[AdminDashboard] Sync failure:", error);
    } finally {
      setLoading(false);
    }
  }, [activeAiAgentId, dateRange, setActiveAiAgentId, setLoading]);

  useEffect(() => {
    if (activeAiAgentId) {
      loadDashboardData();
    }
  }, [activeAiAgentId, dateRange, loadDashboardData]);

  const handleClaimChat = async (sessionId) => {
    setClaimingId(sessionId);
    try {
      await api.request("/widget/handoff/takeover", {
        method: "POST",
        aiAgentId: activeAiAgentId,
        body: JSON.stringify({ session_id: sessionId })
      });

      showToast("Session claimed successfully! Opening Live Chat inbox.", "success");
      setActiveTab("human-agent-chat");
      window.location.href = `/human-agent-chat?session_id=${sessionId}`;
    } catch (error) {
      console.error("[AdminDashboard] Takeover failed:", error);
      showToast("Takeover failed. Try again.", "error");
    } finally {
      setClaimingId(null);
    }
  };

  const handleCreateaiAgent = async (e) => {
    e.preventDefault();
    if (!newAiAgentName.trim()) {
      showToast("AI Agent name is required", "error");
      return;
    }
    setcreatingaiAgent(true);
    try {
      const res = await api.request("/ai-agents", {
        method: "POST",
        body: JSON.stringify({
          name: newAiAgentName,
          description: newAiAgentDesc || "Autonomous support and workspace agent."
        })
      });

      if (res && res.id) {
        showToast("New AI Agent created successfully!", "success");
        setModalOpen(false);
        setNewAiAgentName("");
        setNewAiAgentDesc("");

        if (fetchAiAgents) await fetchAiAgents();
        if (setActiveAiAgentId) {
          setActiveAiAgentId(res.id);
          localStorage.setItem("activeAiAgentId", res.id);
        }

        setActiveTab("instructions");
        window.location.href = "/instructions";
      }
    } catch (error) {
      console.error("[AdminDashboard] Agent creation error:", error);
      showToast(error.message || "Failed to create agent.", "error");
    } finally {
      setcreatingaiAgent(false);
    }
  };

  const getWaitTimeString = (updatedTimestamp) => {
    if (!updatedTimestamp) return "waiting";
    try {
      const updatedTime = new Date(updatedTimestamp * 1000);
      const diffMs = new Date().getTime() - updatedTime.getTime();
      const diffMin = Math.max(1, Math.floor(diffMs / 60000));
      if (diffMin < 60) return `waiting ${diffMin}m`;
      const diffHrs = Math.floor(diffMin / 60);
      return `waiting ${diffHrs}h`;
    } catch (e) {
      return "waiting";
    }
  };

  const calculateRealStats = () => {
    const stats = dashboardData?.overview_stats || dashboardData || {};
    const totalSessions = stats.total_sessions || 0;

    const now = new Date();
    let startTs = 0;
    if (dateRange === "today") {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      startTs = today.getTime() / 1000;
    } else if (dateRange === "7d") {
      startTs = (now.getTime() - 7 * 24 * 60 * 60 * 1000) / 1000;
    } else if (dateRange === "30d") {
      startTs = (now.getTime() - 30 * 24 * 60 * 60 * 1000) / 1000;
    }

    const filteredHandoffs = allHandoffsData.filter(s => s.updated_at >= startTs);
    const periodHandoffsCount = filteredHandoffs.length;
    const totalHandoffsCount = allHandoffsData.length;
    const rawResolutionRate = totalSessions > 0
      ? Math.max(0, Math.min(100, ((totalSessions - periodHandoffsCount) / totalSessions) * 100))
      : 100.0;

    const totalCostValue = Number(dashboardData?.meta?.token_cost_estimate?.total_cost || 0);

    return {
      totalSessions,
      resolutionRate: `${rawResolutionRate.toFixed(1)}%`,
      totalHandoffsCount,
      totalCostStr: `$${totalCostValue.toFixed(2)}`
    };
  };

  const calculated = calculateRealStats();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };
  const greeting = getGreeting();
  const activeAgent = aiAgents?.find(p => p.id === activeAiAgentId);
  const aiAgentName = activeAgent?.name || "Kambaa";

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
              {greeting}, {aiAgentName}
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Workspace command center: live handoffs, agent health, and instant shortcuts.
            </p>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            {/* Date Range Selector */}
            <CustomDropdown
              options={dateRangeOptions}
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              className="z-40"
              buttonClassName="bg-zinc-50 border border-zinc-200 hover:bg-zinc-100 text-xs font-semibold text-zinc-700 px-3 py-2 rounded-lg shadow-2xs min-w-[125px] justify-between cursor-pointer whitespace-nowrap"
              renderOption={(opt, isSelected) => (
                <div className="flex items-center justify-between w-full text-xs whitespace-nowrap">
                  <span>{opt.label}</span>
                  {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 ml-2" />}
                </div>
              )}
            />

            {/* "+ New Agent" Action */}
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] cursor-pointer whitespace-nowrap shrink-0 text-center"
            >
              <Plus size={14} strokeWidth={2.5} className="shrink-0" />
              <span className="whitespace-nowrap text-center">Create AI Agent</span>
            </button>
          </div>
        </div>

        {/* Minimalist Capacity Metrics (Directly matching AiAgents.js) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Total Conversations */}
          <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Conversations</span>
            <div className="text-xl font-bold text-zinc-900 mt-1">{calculated.totalSessions}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              <span className="font-semibold text-zinc-900">{activeChatsCount || 0}</span> active now
            </div>
          </div>

          {/* AI Resolution Rate */}
          <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">AI Resolution</span>
            <div className="text-xl font-bold text-zinc-900 mt-1">{calculated.resolutionRate}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Automated chats</div>
          </div>

          {/* Human Handoffs */}
          <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Human Handoffs</span>
            <div className="text-xl font-bold text-zinc-900 mt-1">{calculated.totalHandoffsCount}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Total escalated</div>
          </div>

          {/* Token Cost */}
          <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
              Token Cost ({dateRangeLabels[dateRange] || dateRange})
            </span>
            <div className="text-xl font-bold text-zinc-900 mt-1">{calculated.totalCostStr}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Computed spend</div>
          </div>
        </div>

        {/* Dual Layout: Elegant Main Content Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
          {/* ════════════════ LEFT COLUMN: Needs Attention ════════════════ */}
          <div className="flex flex-col space-y-3">
            <div className="flex items-center justify-between px-1 h-6">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                  <AlertTriangle size={13} className="text-amber-500" />
                  Needs Attention
                </span>
                {handoffSessions.length > 0 && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                  </span>
                )}
              </div>
              <span className="text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200/60 px-2 py-0.5 rounded-md">
                {handoffSessions.length} Handoff Requests
              </span>
            </div>

            {/* Content Card with AiAgents.js border & header style */}
            <div className="flex-1 bg-white rounded-xl border border-zinc-200 shadow-2xs overflow-hidden flex flex-col">
              <div className="p-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/40 min-h-[57px]">
                <p className="text-xs text-zinc-500">
                  Active widget chats currently flagged for human escalation. Claim them to intervene.
                </p>
                <button
                  onClick={() => {
                    setActiveTab("human-agent-chat");
                    window.location.href = "/human-agent-chat";
                  }}
                  className="text-xs font-semibold text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1 transition-colors cursor-pointer shrink-0 ml-3"
                >
                  <span>Inbox</span>
                  <ArrowUpRight size={13} />
                </button>
              </div>

              {/* Sessions List or Operational Peace */}
              <div className="flex-1 flex flex-col divide-y divide-zinc-100 max-h-[360px] overflow-y-auto">
                {loading ? (
                  <div className="p-6 space-y-3 flex-1">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 animate-pulse">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-zinc-200" />
                          <div className="space-y-1.5">
                            <div className="h-3 w-32 bg-zinc-200 rounded" />
                            <div className="h-2.5 w-24 bg-zinc-100 rounded" />
                          </div>
                        </div>
                        <div className="h-7 w-16 bg-zinc-200 rounded-lg" />
                      </div>
                    ))}
                  </div>
                ) : handoffSessions.length === 0 ? (
                  <div className="p-8 text-center flex-1 flex flex-col items-center justify-center">
                    <CheckCircle2 className="text-emerald-600 shrink-0" size={20} />
                    <h3 className="text-sm font-bold text-zinc-900 mb-0.5">Operational Peace</h3>
                    <p className="text-xs text-zinc-500 max-w-sm mx-auto">
                      AI assistants are handling all conversations smoothly. No live human interventions requested.
                    </p>
                  </div>
                ) : (
                  handoffSessions.map((session) => (
                    <div
                      key={session.session_id}
                      className="p-3.5 sm:p-4 hover:bg-zinc-50/70 transition-colors flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-700 font-bold text-xs flex items-center justify-center shrink-0">
                          {(session.summary || "U").substring(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-zinc-900 text-xs truncate">
                              {session.summary || "Anonymous Session"}
                            </span>
                            {session.handoff_status === "requested" ? (
                              <span className="text-[9px] font-semibold text-rose-700 bg-rose-50 border border-rose-200/60 px-1.5 py-0.2 rounded">
                                Escalated
                              </span>
                            ) : (
                              <span className="text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-200/60 px-1.5 py-0.2 rounded">
                                Claimed
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-zinc-400 mt-0.5">
                            <span
                              className="flex items-center gap-1 cursor-help relative text-zinc-500"
                              onMouseEnter={() => setHoveredSessionId(session.session_id)}
                              onMouseLeave={() => setHoveredSessionId(null)}
                            >
                              <Clock size={11} className="text-zinc-400" />
                              {getWaitTimeString(session.updated_at)}
                              {hoveredSessionId === session.session_id && (
                                <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-zinc-900 text-white rounded text-[10px] whitespace-nowrap z-50 shadow-md">
                                  {new Date(session.updated_at * 1000).toLocaleString()}
                                </div>
                              )}
                            </span>
                            <span>•</span>
                            <span className="truncate max-w-[200px] text-zinc-500">
                              {session.handoff_reason || "Direct user trigger"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {session.handoff_status === "requested" ? (
                        <button
                          onClick={() => handleClaimChat(session.session_id)}
                          disabled={claimingId === session.session_id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-900 text-white text-xs font-semibold rounded-lg hover:bg-zinc-800 disabled:opacity-50 transition-colors shrink-0 cursor-pointer shadow-2xs"
                        >
                          <span>Claim</span>
                          <ArrowRight size={11} />
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setActiveTab("human-agent-chat");
                            window.location.href = `/human-agent-chat?session_id=${session.session_id}`;
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-200 text-zinc-700 bg-white text-xs font-semibold rounded-lg hover:bg-zinc-50 transition-colors shrink-0 cursor-pointer shadow-2xs"
                        >
                          <span>View</span>
                          <ArrowRight size={11} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* ════════════════ RIGHT COLUMN: Quick Actions ════════════════ */}
          <div className="flex flex-col space-y-3">
            <div className="flex items-center justify-between px-1 h-6">
              <span className="text-xs font-bold text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles size={13} className="text-zinc-500" />
                Quick Actions
              </span>
            </div>

            <div className="flex-1 bg-white rounded-xl border border-zinc-200 shadow-2xs overflow-hidden flex flex-col justify-between">
              <div>
                <div className="p-4 border-b border-zinc-100 bg-zinc-50/40 min-h-[57px] flex items-center">
                  <p className="text-xs text-zinc-500">
                    Execute setup, knowledge training, and workspace integrations directly.
                  </p>
                </div>

                <div className="p-4 grid grid-cols-2 gap-3">
                  {/* Action 1: API Tools */}
                  <button
                    onClick={() => {
                      setActiveTab("api-tools");
                      window.location.href = "/api-tools";
                    }}
                    className="p-3.5 bg-zinc-50/70 hover:bg-zinc-100/80 border border-zinc-200/70 rounded-xl text-left transition-all cursor-pointer group hover:shadow-2xs"
                  >
                    <div className="text-zinc-700 mb-2.5 transition-transform group-hover:scale-105">
                      <Wrench size={18} strokeWidth={2} />
                    </div>
                    <h3 className="text-xs font-bold text-zinc-900 group-hover:text-black">API Tools</h3>
                    <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-1">Manage external APIs</p>
                  </button>

                  {/* Action 2: Document Upload */}
                  <button
                    onClick={() => {
                      setActiveTab("documents");
                      window.location.href = "/documents";
                    }}
                    className="p-3.5 bg-zinc-50/70 hover:bg-zinc-100/80 border border-zinc-200/70 rounded-xl text-left transition-all cursor-pointer group hover:shadow-2xs"
                  >
                    <div className="text-zinc-700 mb-2.5 transition-transform group-hover:scale-105">
                      <FileText size={18} strokeWidth={2} />
                    </div>
                    <h3 className="text-xs font-bold text-zinc-900 group-hover:text-black">Upload Docs</h3>
                    <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-1">Train custom data</p>
                  </button>

                  {/* Action 3: Integrations */}
                  <button
                    onClick={() => {
                      setActiveTab("integrations");
                      window.location.href = "/integrations";
                    }}
                    className="p-3.5 bg-zinc-50/70 hover:bg-zinc-100/80 border border-zinc-200/70 rounded-xl text-left transition-all cursor-pointer group hover:shadow-2xs"
                  >
                    <div className="text-zinc-700 mb-2.5 transition-transform group-hover:scale-105">
                      <Plug size={18} strokeWidth={2} />
                    </div>
                    <h3 className="text-xs font-bold text-zinc-900 group-hover:text-black">Integrations</h3>
                    <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-1">Slack, WhatsApp, Helpdesk</p>
                  </button>

                  {/* Action 4: Invite Teammate */}
                  <button
                    onClick={() => {
                      setActiveTab("contacts");
                      window.location.href = "/user-management";
                    }}
                    className="p-3.5 bg-zinc-50/70 hover:bg-zinc-100/80 border border-zinc-200/70 rounded-xl text-left transition-all cursor-pointer group hover:shadow-2xs"
                  >
                    <div className="text-zinc-700 mb-2.5 transition-transform group-hover:scale-105">
                      <UserPlus size={18} strokeWidth={2} />
                    </div>
                    <h3 className="text-xs font-bold text-zinc-900 group-hover:text-black">Team Members</h3>
                    <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-1">Manage seat access</p>
                  </button>
                </div>
              </div>

              <div className="px-4 py-2.5 border-t border-zinc-100 bg-zinc-50/30 flex items-center justify-between text-[11px] text-zinc-400 font-medium">
              </div>
            </div>
          </div>
        </div>

        {/* ── "+ New Agent" Modal ── */}
        {modalOpen && ReactDOM.createPortal(
          <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl border border-zinc-200 p-5 sm:p-6 w-full max-w-md shadow-xl relative animate-in fade-in zoom-in-95 duration-150">
              <button
                onClick={() => setModalOpen(false)}
                className="absolute right-4 top-4 p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
              >
                <X size={16} />
              </button>

              <div className="flex items-center gap-2.5 mb-4">
                <Sparkles className="text-zinc-700 shrink-0" size={18} />
                <div>
                  <h2 className="text-sm font-bold text-zinc-900">New AI Agent</h2>
                  <p className="text-xs text-zinc-500">Initialize a dynamic AI agent identity</p>
                </div>
              </div>

              <form onSubmit={handleCreateaiAgent} className="space-y-3.5">
                <div>
                  <label className="block text-[11px] font-medium text-zinc-700 mb-1">
                    AI Agent Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Sales Onboarding Expert"
                    value={newAiAgentName}
                    onChange={(e) => setNewAiAgentName(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-zinc-700 mb-1">
                    Description
                  </label>
                  <textarea
                    placeholder="Describe the operational duties or role parameters..."
                    rows={3}
                    value={newAiAgentDesc}
                    onChange={(e) => setNewAiAgentDesc(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400 resize-none"
                  />
                </div>

                <div className="flex gap-2 pt-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingaiAgent || !newAiAgentName.trim()}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white font-semibold text-xs rounded-lg transition-colors cursor-pointer"
                  >
                    {creatingaiAgent ? (
                      <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    ) : (
                      <span>Deploy AI Agent</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}
